import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Trophy, Users, TrendingUp, DollarSign, Target } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";

const BAR_COLORS = ["#0ea5e9", "#22c55e", "#f59e0b", "#a855f7", "#ef4444", "#14b8a6", "#f97316", "#6366f1"];

export const UsersPerformancePage = () => {
  const { user: currentUser } = useAuth();
  const [data, setData] = useState({ users: [], totals: {}, year: null });
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getUsersPerformance({ year });
      setData(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur chargement"));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Admin-only page
  if (currentUser && currentUser.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= new Date().getFullYear() - 5; y--) years.push(y);

  // Prepare chart data
  const chartData = data.users
    .filter(u => u.total_orders > 0)
    .map((u, i) => ({
      name: u.name || u.email.split("@")[0],
      ca: u.total_ca_ht,
      paid: u.total_paid,
      orders: u.total_orders,
      fill: BAR_COLORS[i % BAR_COLORS.length]
    }));

  const top = data.users.find(u => u.total_orders > 0);

  return (
    <div className="space-y-6" data-testid="users-performance-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Performance utilisateurs</h1>
          <p className="text-muted-foreground">Comparez les performances de tous vos vendeurs / utilisateurs</p>
        </div>
        <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* KPIs globaux */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Utilisateurs</div>
                <div className="text-3xl font-bold">{data.totals.total_users || 0}</div>
              </div>
              <Users className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Commandes totales</div>
                <div className="text-3xl font-bold">{data.totals.total_orders || 0}</div>
              </div>
              <Target className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">CA total HT</div>
                <div className="text-2xl font-bold">{formatCurrency(data.totals.total_ca_ht || 0)}</div>
              </div>
              <TrendingUp className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Total encaissé</div>
                <div className="text-2xl font-bold">{formatCurrency(data.totals.total_paid || 0)}</div>
              </div>
              <DollarSign className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {top && top.total_ca_ht > 0 && (
        <Card className="border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
          <CardContent className="pt-6 flex items-center gap-4">
            <Trophy className="h-12 w-12 text-yellow-500" />
            <div>
              <div className="text-sm text-muted-foreground">Top performer {year}</div>
              <div className="text-2xl font-bold">{top.name || top.email}</div>
              <div className="text-sm">
                {formatCurrency(top.total_ca_ht)} HT sur {top.total_orders} commande(s) • {top.ca_completion_rate}% réalisées
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Comparaison CA par utilisateur</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="ca" name="CA total HT" fill="#0ea5e9" />
                <Bar dataKey="paid" name="Encaissé" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Detailed table */}
      <Card>
        <CardHeader>
          <CardTitle>Détails par utilisateur</CardTitle>
        </CardHeader>
        <CardContent>
          {data.users.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucun utilisateur à afficher.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead className="text-right">Commandes</TableHead>
                    <TableHead className="text-right">CA HT</TableHead>
                    <TableHead className="text-right">CA réalisé</TableHead>
                    <TableHead className="text-right">Encaissé</TableHead>
                    <TableHead className="text-right">Moy / cmd</TableHead>
                    <TableHead className="text-right">Clients</TableHead>
                    <TableHead className="text-right">% complété</TableHead>
                    <TableHead>Statuts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.users.map((u, idx) => (
                    <TableRow key={u.user_id} data-testid={`perf-row-${u.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {idx === 0 && u.total_ca_ht > 0 && <Trophy className="h-4 w-4 text-yellow-500" />}
                          <div>
                            <div className="font-medium">{u.name || "-"}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{u.total_orders}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(u.total_ca_ht)}</TableCell>
                      <TableCell className="text-right text-green-700">{formatCurrency(u.ca_completed)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(u.total_paid)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatCurrency(u.avg_order_ht)}</TableCell>
                      <TableCell className="text-right">{u.unique_clients}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={u.ca_completion_rate > 50 ? "default" : "outline"}>
                          {u.ca_completion_rate}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap text-xs">
                          {u.pending_count > 0 && <Badge variant="outline" className="bg-amber-50">⏱ {u.pending_count}</Badge>}
                          {u.in_progress_count > 0 && <Badge variant="outline" className="bg-blue-50">▶ {u.in_progress_count}</Badge>}
                          {u.completed_count > 0 && <Badge variant="outline" className="bg-green-50">✓ {u.completed_count}</Badge>}
                          {u.on_hold_count > 0 && <Badge variant="outline" className="bg-slate-100">⏸ {u.on_hold_count}</Badge>}
                          {u.subcontracted_count > 0 && <Badge variant="outline" className="bg-purple-50">🏗 {u.subcontracted_count}</Badge>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPerformancePage;
